import { useState, createContext, type ReactNode } from 'react';

import {
    SelectProps as AriaSelectProps,
    Select as AriaSelect,
    ValidationResult,
} from 'react-aria-components';

import { Placement } from '@react-types/overlays';

import { cx } from '@/utils/cx';

import SelectValue from '@/components/select/SelectValue';
import SelectPopover from '@/components/select/SelectPopover';
import SelectListBox, { SelectItem } from '@/components/select/SelectListBox';
import SelectListBoxItem from '@/components/select/SelectListBoxItem';
import selectRecipe, { SelectRecipeProps } from '@/components/select/select.styles';
import { Label } from '@/components/field/label/Label';
import { Description } from '@/components/field/description/Description';
import { FieldError } from '@/components/field/fieldError/FieldError';
import SelectButton from '@/components/select/SelectButton';

export const SelectVariantContext = createContext<SelectRecipeProps>(undefined);

type SelectProps<T extends SelectItem> = {
    className?: string;
    items?: T[];
    popoverPlacement?: Placement;
    label?: string;
    description?: string;
    errorMessage?: string | ((validation: ValidationResult) => string);
    buttonClassName?: string;
    descriptionClassName?: string;
    labelClassName?: string;
    popoverClassName?: string;
    listBoxItemClassName?: string;
    errorClassName?: string;
    children?: ReactNode;
} & AriaSelectProps<HTMLDivElement> &
    SelectRecipeProps;

function Select<T extends SelectItem>({
    className,
    items,
    popoverPlacement = 'bottom',
    label,
    description,
    errorMessage,
    buttonClassName,
    descriptionClassName,
    labelClassName,
    popoverClassName,
    listBoxItemClassName,
    errorClassName,
    children,
    ...rest
}: SelectProps<T>) {
    const [open, setOpen] = useState(false);
    const [variantProps, localProps] = selectRecipe.splitVariantProps(rest);
    const recipeClassNames = selectRecipe(variantProps);
    const { size } = rest;

    return (
        <AriaSelect
            {...localProps}
            isOpen={open}
            onOpenChange={setOpen}
            className={cx('group', recipeClassNames.root, className)}>
            <SelectVariantContext.Provider value={variantProps}>
                <Label size={size} className={cx(recipeClassNames.label, labelClassName)}>
                    {label}
                </Label>
                <SelectButton className={buttonClassName} {...variantProps}>
                    <SelectValue />
                </SelectButton>
                <Description
                    size={size}
                    className={cx(recipeClassNames.description, descriptionClassName)}>
                    {description}
                </Description>
                <FieldError size={size} className={cx(recipeClassNames.error, errorClassName)}>
                    {errorMessage}
                </FieldError>
                <SelectPopover
                    {...variantProps}
                    placement={popoverPlacement}
                    offset={0}
                    className={popoverClassName}>
                    <SelectListBox
                        {...variantProps}
                        className={cx(recipeClassNames.listBox)}
                        items={items}>
                        {children}
                        {items?.map((item) => (
                            <SelectListBoxItem
                                key={item.id}
                                {...variantProps}
                                text={item.name}
                                id={item.id}
                                className={listBoxItemClassName}
                            />
                        ))}
                    </SelectListBox>
                </SelectPopover>
            </SelectVariantContext.Provider>
        </AriaSelect>
    );
}

export { Select };
export type { SelectProps };
